// AttestationTravail.jsx – Design professionnel amélioré
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faDownload, faTimes, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import defaultLogo from '../../../assetes/logos.png'; // Logo de fallback

const API_URL = `${API_CONFIG2.apiUrl}employe/api_attestation.php`;

const AttestationTravail = ({ employeId }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [attestationData, setAttestationData] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const attestationRef = useRef(null);

  useEffect(() => {
    if (employeId) loadData();
  }, [employeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}?employe_id=${employeId}`);
      const json = await res.json();
      if (json.status === 'success') {
        setAttestationData(json.data);
      } else {
        alert('Erreur : ' + (json.message || 'Données non trouvées'));
      }
    } catch (err) {
      alert('Erreur réseau : ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!attestationRef.current) return;
    setGeneratingPDF(true);
    try {
      const canvas = await html2canvas(attestationRef.current, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Attestation_${attestationData.employe.nom || 'employe'}.pdf`);
    } catch (error) {
      console.error('Erreur PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const employe = attestationData?.employe;
  const entreprise = attestationData?.entreprise || {};
  const logoUrl = entreprise.logoUrl || defaultLogo;

  return (
    <>
      {/* Bouton d'ouverture */}
      <button
        className="btn-attestation"
        onClick={() => setShowModal(true)}
        disabled={loading}
        style={{
          padding: '12px 28px',
          borderRadius: '40px',
          border: 'none',
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          color: '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.95rem',
          boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      >
        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFilePdf} />}
        {loading ? 'Chargement...' : '📄 Attestation de travail'}
      </button>

      {/* Modal */}
      {showModal && attestationData && (
        <div className="attestation-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="attestation-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="attestation-modal-header">
              <h3>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22c55e', marginRight: '8px' }} />
                Attestation de travail
              </h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="attestation-modal-body" ref={attestationRef}>
              {/* ===== CONTENU DE L'ATTESTATION ===== */}
              <div className="attestation-paper">
                {/* En-tête avec logo */}
                <div className="attestation-header">
                  <div className="header-left">
                   
                  </div>
                  <div className="header-right">
                     <img src={logoUrl} alt="Logo" className="logo-img" crossOrigin="anonymous" />
                   
                    <p className="coords">
                      {entreprise.adresse && <span>{entreprise.adresse}</span>}
                      {entreprise.telephone && <span> | Tél: {entreprise.telephone}</span>}
                      {entreprise.email && <span> | Email: {entreprise.email}</span>}
                    </p>
                    <p className="coords small">
                      {entreprise.rccm && <span>RCCM: {entreprise.rccm}</span>}
                      {entreprise.idnat && <span> | ID.NAT: {entreprise.idnat}</span>}
                      {entreprise.nimpot && <span> | N° Impôt: {entreprise.nimpot}</span>}
                    </p>
                  </div>
                </div>

                <div className="separator"></div>

                <h2 className="title">ATTESTATION DE TRAVAIL</h2>

                <div className="body-text">
                  <p>
                    Je soussigné, <strong>{entreprise.nom || 'l\'entreprise'}</strong>, représentée par son Gérant,
                    certifie par la présente que <strong>
                      {employe.prenom} {employe.nom} {employe.postnom || ''}, </strong> né(e) le <strong>{formatDate(employe.date_naissance)}</strong> à <strong>{(employe.lieu_naissance)}</strong> et
                      de nationalité <strong> {employe.nationalite}</strong> et residant dans la ville de <strong> {employe.ville} </strong> Commune de <strong>{employe.commune}</strong>  <strong>{employe.adresse}</strong> est employé(e) dans notre société depuis le <strong>{formatDate(employe.date_embauche)}</strong>.
                  </p>
                  <p>
                    Il/Elle occupe actuellement le poste de <strong> {employe.fonction || 'Non spécifié'}</strong> au sein du service <strong>{employe.service || 'Non spécifié'}</strong>.
                  </p>
                  <p>
                    Son salaire mensuel brut s'élève à <strong>
                      {Number(employe.salaire_base).toLocaleString('fr-FR')} CDF
                    </strong>.
                  </p>
                  <p className="clause">
                    La présente attestation est délivrée à l’intéressé(e) pour servir et valoir ce que de droit.
                  </p>
                </div>

                <div className="signature-block">
                  <div>
                    <p className="date-location">Fait à Kinshasa, le {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    <p className="signature-label">Le Gérant,</p>
                    {entreprise.signatureUrl && (
                      <img src={entreprise.signatureUrl} alt="Signature" className="signature-img" crossOrigin="anonymous" />
                    )}
                    <div className="signature-line"></div>
                    <p className="signature-caption">Signature et cachet de l'entreprise</p>
                  </div>
                </div>

                <div className="footer">
                  {entreprise.nom} – {entreprise.adresse} – Tél: {entreprise.telephone}
                </div>
              </div>
            </div>

            <div className="attestation-modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Fermer</button>
              <button className="btn-primary" onClick={handleDownloadPDF} disabled={generatingPDF}>
                {generatingPDF ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Génération...</>
                ) : (
                  <><FontAwesomeIcon icon={faDownload} /> Télécharger PDF</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS améliorés */}
      <style jsx>{`
        .attestation-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        .attestation-modal-content {
          background: #ffffff;
          border-radius: 28px;
          max-width: 880px;
          width: 100%;
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 40px 100px rgba(0, 0, 0, 0.4);
          overflow: hidden;
        }
        .attestation-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 28px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .attestation-modal-header h3 {
          margin: 0;
          font-size: 1.2rem;
          color: #0f172a;
          font-weight: 600;
          display: flex;
          align-items: center;
        }
        .close-btn {
          background: none;
          border: none;
          font-size: 1.6rem;
          cursor: pointer;
          color: #94a3b8;
          transition: 0.2s;
          padding: 0 4px;
        }
        .close-btn:hover {
          color: #ef4444;
          transform: rotate(90deg);
        }
        .attestation-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 24px 28px 16px;
          background: #f1f5f9;
          display: flex;
          justify-content: center;
        }
        .attestation-paper {
          max-width: 750px;
          width: 100%;
          padding: 40px 44px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
          font-family: 'Georgia', 'Times New Roman', serif;
          color: #1e293b;
          line-height: 1.7;
          transition: all 0.2s;
        }
        /* En-tête */
        .attestation-header {
          display: flex;
          align-items: center;
          gap: 24px;
          margin-bottom: 18px;
        }
        .header-left {
          flex-shrink: 0;
        }
        .logo-img {
          max-width: 220px;
          max-height: 160px;
          object-fit: contain;
        }
        .header-right {
          flex: 1;
          text-align: left;
        }
        .header-right h1 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 4px 0;
          color: #0f172a;
          letter-spacing: 0.5px;
        }
        .coords {
          font-size: 13px;
          color: #475569;
          margin: 2px 0;
        }
        .coords.small {
          font-size: 12px;
          color: #64748b;
        }
        .separator {
          height: 3px;
          background: linear-gradient(to right, #1e3a8a, #3b82f6, #1e3a8a);
          border-radius: 4px;
          margin: 16px 0 22px;
        }
        .title {
          text-align: center;
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: 2px;
          margin: 8px 0 28px;
          text-transform: uppercase;
          position: relative;
        }
        .title::after {
          content: '';
          display: block;
          width: 80px;
          height: 3px;
          background: #1e3a8a;
          margin: 8px auto 0;
          border-radius: 2px;
        }
        .body-text {
          font-size: 15.5px;
          text-align: justify;
          color: #1e293b;
        }
        .body-text p {
          margin: 0 0 14px 0;
        }
        .body-text strong {
          color: #0f172a;
        }
        .clause {
          font-style: italic;
          color: #334155;
          margin-top: 6px;
          border-left: 3px solid #3b82f6;
          padding-left: 16px;
        }
        .signature-block {
          margin-top: 38px;
          display: flex;
          justify-content: flex-end;
          text-align: center;
        }
        .signature-block > div {
          max-width: 280px;
        }
        .date-location {
          font-weight: 500;
          font-size: 14px;
          margin-bottom: 18px;
          color: #1e293b;
        }
        .signature-label {
          font-weight: 600;
          font-size: 15px;
          margin: 10px 0 8px;
        }
        .signature-img {
          max-width: 160px;
          max-height: 60px;
          object-fit: contain;
          display: block;
          margin: 4px auto 6px;
        }
        .signature-line {
          width: 180px;
          height: 1px;
          background: #94a3b8;
          margin: 6px auto 2px;
        }
        .signature-caption {
          font-size: 12px;
          color: #64748b;
          margin-top: 2px;
        }
        .footer {
          margin-top: 34px;
          border-top: 1px solid #e2e8f0;
          padding-top: 16px;
          font-size: 11px;
          color: #94a3b8;
          text-align: center;
          letter-spacing: 0.3px;
        }
        .attestation-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 14px;
          padding: 16px 28px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-shrink: 0;
          flex-wrap: wrap;
        }
        .btn-primary, .btn-secondary {
          padding: 10px 24px;
          border-radius: 40px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
        }
        .btn-primary {
          background: #3b82f6;
          color: #fff;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .btn-primary:hover:not(:disabled) {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .btn-secondary {
          background: #e2e8f0;
          color: #1e293b;
        }
        .btn-secondary:hover {
          background: #cbd5e1;
          transform: translateY(-2px);
        }
        @media (max-width: 640px) {
          .attestation-modal-content {
            max-height: 100vh;
            border-radius: 0;
            max-width: 100%;
          }
          .attestation-modal-body {
            padding: 12px;
          }
          .attestation-paper {
            padding: 24px 16px;
          }
          .attestation-header {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }
          .header-right {
            text-align: center;
          }
          .header-right h1 {
            font-size: 18px;
          }
          .title {
            font-size: 20px;
          }
          .signature-block {
            justify-content: center;
          }
          .attestation-modal-footer {
            flex-direction: column-reverse;
          }
          .btn-primary, .btn-secondary {
            justify-content: center;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default AttestationTravail;